import { useStore } from "@/core/utils/hooks";

import type { APIPermissionProfilePreset } from "@/core/data_sources/models/APIPermissionProfilePreset";
import {
  AdminPanel,
  AdminMiniList,
  AdminMiniRow,
  Banner,
} from "@/core/ui/molecules";

import { PermissionProfilePresetsStore } from "@/app/access-presets/data_sources/store";

export function PresetUsageDrawer({
  onClose,
  preset,
}: {
  readonly onClose: () => void;
  readonly preset: APIPermissionProfilePreset;
}) {
  const { hooks } = useStore(PermissionProfilePresetsStore.instance);
  const { data, loading } = hooks.usePresetUsageService({
    id: preset.getData().id!,
    page: 1,
    page_size: 1,
  });
  const usage = data?.data;

  return (
    <div className="flex h-full flex-col bg-surface">
      <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-5">
        <div>
          <h2 className="text-lg font-extrabold text-ink">
            Usage · {preset.getData().name}
          </h2>
          <p className="mt-1 text-[13px] text-ink-muted">
            Plans whose current config references this preset.
          </p>
        </div>
        <button
          className="text-2xl text-ink-subtle"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        {loading && !usage ? (
          <p className="text-xs text-ink-muted">Loading…</p>
        ) : null}
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div className="rounded-panel border border-border px-4 py-3">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.06em] text-ink-muted">
              Plans using preset
            </div>
            <div className="text-2xl font-extrabold text-ink">
              {usage?.plans_using_preset?.length ?? 0}
            </div>
          </div>
          <div className="rounded-panel border border-border px-4 py-3">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.06em] text-ink-muted">
              Default assignments
            </div>
            <div className="text-2xl font-extrabold text-ink">
              {usage?.default_assignments_count ?? 0}
            </div>
          </div>
        </div>

        <AdminPanel>
          <h3 className="mb-3 text-[15px] font-extrabold text-ink">Plans</h3>
          {usage?.plans_using_preset && usage.plans_using_preset.length > 0 ? (
            <AdminMiniList>
              {usage.plans_using_preset.map((plan) => (
                <AdminMiniRow key={plan.plan_code}>
                  <span>
                    <strong>{plan.name}</strong>
                    <span className="block text-xs text-ink-muted">
                      {plan.plan_code} · v{plan.current_version ?? 0} ·{" "}
                      {plan.status}
                    </span>
                  </span>
                </AdminMiniRow>
              ))}
            </AdminMiniList>
          ) : (
            <Banner>No plans reference this preset yet.</Banner>
          )}
        </AdminPanel>
      </div>
    </div>
  );
}
